// Warnings only (spec 08 §3): all-caps subject, > 3 links, missing alt, image-only body, spam words.
import { htmlToText } from "./htmlToText.ts";
import { countTrackableLinks } from "./trackLinks.ts";

export type LintCode =
  | "subject_all_caps"
  | "too_many_links"
  | "image_missing_alt"
  | "image_only"
  | "spam_word";
export type LintWarning = { code: LintCode; detail?: string };

export const SPAM_WORDS: readonly string[] = Object.freeze([
  "gratuit",
  "gratuite",
  "gratuits",
  "gratuites",
  "100 %",
  "100%",
  "garanti",
  "garantie",
  "garantis",
  "garanties",
  "urgent",
  "urgente",
  "cliquez ici",
  "argent facile",
  "offre limitée",
  "gagnez",
  "promotion",
  "sans frais",
  "aucun risque",
  "résultat garanti",
  "!!!",
  "free",
  "act now",
  "click here",
  "winner",
  "guaranteed",
  "risk-free",
  "no obligation",
  "$$$",
]);

const escapeRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\$]/g, "\\$&");
const WORD_CHAR = /[\p{L}\d]/u;
// Whole-word match: a boundary is enforced only on a side whose edge character is a letter or
// digit (so "free" does not hit "freedom", but "!!!" and "$$$" match when glued to a word).
function wordRegExp(w: string): RegExp {
  const chars = [...w];
  const before = WORD_CHAR.test(chars[0]) ? "(?<![\\p{L}\\d])" : "";
  const after = WORD_CHAR.test(chars[chars.length - 1])
    ? "(?![\\p{L}\\d])"
    : "";
  return new RegExp(`${before}${escapeRe(w)}${after}`, "u");
}
const WORD_RES = new Map(SPAM_WORDS.map((w) => [w, wordRegExp(w)]));

export function lintEmail(i: { subject: string; html: string }): LintWarning[] {
  const w: LintWarning[] = [];
  const letters = i.subject.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, "");
  if (
    letters.length >= 4 &&
    letters === letters.toUpperCase() &&
    letters !== letters.toLowerCase()
  ) {
    w.push({ code: "subject_all_caps" });
  }
  const links = countTrackableLinks(i.html);
  if (links > 3) w.push({ code: "too_many_links", detail: String(links) });
  const imgs = i.html.match(/<img\b[^>]*>/gi) ?? [];
  const noAlt = imgs.filter(
    (t) => !/\balt\s*=\s*("[^"]+"|'[^']+')/i.test(t),
  ).length;
  if (noAlt) w.push({ code: "image_missing_alt", detail: String(noAlt) });
  if (
    imgs.length > 0 &&
    htmlToText(i.html.replace(/<img\b[^>]*>/gi, "")).replace(/\s+/g, "")
      .length < 20
  ) {
    w.push({ code: "image_only" });
  }
  const hay = `${i.subject}\n${htmlToText(i.html)}`
    .replace(/[\s\u00a0\u202f]+/g, " ")
    .toLowerCase();
  for (const word of SPAM_WORDS) {
    if (WORD_RES.get(word)?.test(hay))
      w.push({ code: "spam_word", detail: word });
  }
  return w;
}
